/**
 * 简体中文 "near me" copy — wave 2: the remaining (non-flagship) clusters.
 *
 * Hand-authored (zero AI credits at build or runtime) and factually aligned
 * with ./registry.ts and ./en-copy-2.ts:
 *  - 泰国不是《海牙认证公约》成员国；赴外使用的泰国文件仍需经外交部领事司
 *    认证，必要时再送目的国驻泰使馆认证。
 *  - 不公布价格（按案报价）、不承诺办理时限、不承诺一定被接受——是否接受
 *    由接收机关决定。
 *  - 顾问式定位：我们先厘清顺序与证据，再执行，而不是单纯代办。
 */

import type { NearServiceKey } from "./registry";
import type { NearLocaleCopy } from "./zh-copy";

export const NEAR_ZH2_KEYS = [
  "marriage",
  "longstay",
  "corporate",
  "immigration",
  "estate",
  "civilreg",
  "family",
  "accounting",
  "interpreter",
  "embassy",
  "ip",
  "litigation",
  "pdpa",
  "misc-official",
  "council",
  "due-diligence",
  "pension",
  "work-permit",
  "expert",
] as const satisfies readonly NearServiceKey[];

export type NearZh2Key = (typeof NEAR_ZH2_KEYS)[number];

export const NEAR_ZH2_COPY: Record<NearZh2Key, NearLocaleCopy> = {
  marriage: {
    label: "泰外跨国结婚文件服务",
    definition:
      "在泰国办理跨国结婚不是一次预约就能完成，而是一条文件链：先在本国驻泰使馆取得可婚证明（单身证明），再做泰文认证翻译，经外交部领事司认证，最后到区公所（Amphoe）登记结婚。",
    chain: [
      "确认您所属使馆目前的可婚证明／单身证明流程与预约规则。",
      "取得使馆证明后，交由认证译员翻译成泰文。",
      "将译文送外交部领事司（Legalization Division）认证。",
      "双方本人到区公所办理结婚登记。",
      "办理结婚证书副本、翻译与认证，以便在国外使用。",
    ],
    checklist: [
      "外籍一方护照及入境章页。",
      "泰籍一方身份证与户口本（Tabien Baan）。",
      "使馆出具的可婚证明或单身证明。",
      "曾有婚姻者需离婚证明或配偶死亡证明。",
      "区公所登记需两名见证人。",
    ],
    pitfalls: [
      "外交部认证尚未完成就先预约区公所。",
      "使馆证明上的姓名拼写与护照不一致。",
      "缺少前次婚姻终止的证明文件。",
      "误以为泰国结婚证书无需翻译与认证即可在国外使用。",
    ],
    remote: "翻译、认证与流程安排都可由我们远程处理；使馆预约与登记本身需双方到场。",
  },
  longstay: {
    label: "长期居留与退休签延期文件服务",
    definition:
      "长期居留延期由移民局根据所提交的证据决定。我们负责文件准备与顾问建议：把证据组按类别对齐，并整理成移民局接受的格式。",
    chain: [
      "确认正确的延期类别与目前要求的证据清单。",
      "对照您现有文件做缺口分析。",
      "按受理格式准备财务、住址与保险证明。",
      "按递交顺序整理档案，并附递交清单。",
      "递交时协助，并回应补充证据的要求。",
    ],
    checklist: [
      "护照及现行签证、TM.6／出入境记录。",
      "TM.30 住址申报与租约或产权证明。",
      "符合格式与存期要求的银行文件。",
      "该类别要求的健康保险文件。",
      "家庭类别需结婚或亲属关系文件。",
    ],
    pitfalls: [
      "银行证明未达要求的存款期限。",
      "缺少 TM.30 申报——延期前会先核查。",
      "在允许停留期即将到期时才递交。",
      "以为延期可以保证通过——决定权在审理官员。",
    ],
    remote: "档案准备与审核可远程完成；移民局预约需本人到场。",
  },
  corporate: {
    label: "公司文件与企业认证服务",
    definition:
      "公司文件工作涵盖公司证明书、DBD 登记摘录、董事会决议、授权书及其赴外使用的认证——常用于开户、投标、代理授权或设立海外子公司。",
    chain: [
      "确认接收方要求的确切文件清单与出具时效。",
      "取得最新的 DBD 摘录与公司证明书。",
      "按对方接受的用语准备决议或授权书。",
      "由公证律师认证签字与副本。",
      "赴外使用时办理外交部认证与目的国驻泰使馆认证。",
    ],
    checklist: [
      "公司证明书与 DBD 登记摘录（近期出具）。",
      "股东名册与经营目的摘录。",
      "授权董事的护照或泰国身份证。",
      "决议或合同的最终版本草案。",
      "接收方的书面文件要求。",
    ],
    pitfalls: [
      "摘录出具日期超出对方认可的有效期。",
      "在认证预约前先行签署。",
      "由公司证明书上无权限的董事签署决议。",
      "译文遗漏泰文摘录上的印章与钢印文字。",
    ],
    remote: "除签字见证外，其余环节均可远程办理。",
  },
  immigration: {
    label: "移民文件准备与顾问服务",
    definition:
      "移民类申请——延期、回国许可、身份变更、各类申报——各有其证据清单。我们负责准备与排序，最终由泰国移民局决定。",
    chain: [
      "确认申请类型、管辖办事处与证据清单。",
      "核对现行停留期与过往申请的一致性。",
      "准备并认证支持文件。",
      "按递交顺序整理，并附柜台用清单。",
      "跟进并回应补充证据要求。",
    ],
    checklist: [
      "护照及全部签证与盖章页。",
      "TM.30 住址申报与住所证明。",
      "与类别相关的工作、学习或家庭文件。",
      "符合现行规格的照片。",
      "由代理递交时需授权书。",
    ],
    pitfalls: [
      "递交到无管辖权的办事处。",
      "TM.30、租约与申请表上的地址不一致。",
      "在收集证据期间让停留期逾期。",
      "依据网上过期清单，而非办事处当前清单。",
    ],
    remote: "准备与顾问可远程；柜台预约需本人到场。",
  },
  estate: {
    label: "遗嘱、遗产与继承文件服务",
    definition:
      "泰国遗产事务包含法院与登记两条线：先有遗嘱（或依无遗嘱法定继承），再由法院指定遗产管理人，然后在各资产的登记机关分别办理过户。跨境遗产还需翻译与认证。",
    chain: [
      "梳理遗产：资产、所在地、继承人以及是否有遗嘱。",
      "确认各类资产是否需要法院指定的管理人。",
      "整理身份与继承关系证明，境外文件附认证翻译。",
      "由律师提出管理人申请并出庭。",
      "凭法院命令在相关登记机关办理各项资产过户。",
    ],
    checklist: [
      "死亡证明；境外出具者需认证翻译。",
      "遗嘱（如有）及原件保管地点。",
      "继承关系证明：户口本、出生与结婚证明。",
      "资产证明：地契、银行对账单、股东名册。",
      "全体继承人的护照或身份证。",
    ],
    pitfalls: [
      "在取得管理人命令前就尝试办理银行或土地过户。",
      "境外文件缺少经认证的泰文认证翻译。",
      "遗漏继承人，可能导致分配无效。",
      "以为境外遗嘱可直接处分泰国土地而无需泰国程序。",
    ],
    remote: "文件整理与起草可远程；开庭由律师出席，仅在法院要求时需您本人到场。",
  },
  civilreg: {
    label: "户籍与民事登记文件服务",
    definition:
      "民事登记涵盖区公所保管的出生、结婚、离婚、死亡、改名与户籍记录。每一项更正或摘录都有各自的证据清单，赴外使用还需翻译与认证。",
    chain: [
      "确认记录类型以及保管该记录的区公所。",
      "申请认证摘录，或提出更正申请。",
      "整理该办事处列明的支持证据。",
      "为海外使用办理认证翻译。",
      "如有要求，办理外交部认证与使馆认证。",
    ],
    checklist: [
      "泰国身份证与户口本；外籍人士提供护照。",
      "已持有的证书或摘录（如有）。",
      "支持更正内容的证据（学校、医院或法院文件）。",
      "由我们代为申请时需授权书。",
    ],
    pitfalls: [
      "向未保管原始记录的办事处申请。",
      "更正申请缺少证明正确数据的文件。",
      "先翻译却随后重新出具摘录，导致重做一轮。",
      "同一家庭成员文件的拼写转写不一致。",
    ],
    remote: "申请与跟进可由我们代办；部分更正需记录本人到场。",
  },
  family: {
    label: "家事法律文件服务",
    definition:
      "家事事务——离婚、子女监护、认领、监护人指定、婚前协议——依情形在区公所（协议方式）或法院办理。先选对路径，可避免大部分延误。",
    chain: [
      "评估该事项能否以协议方式在区公所完成，或必须走法院。",
      "以可执行的用语起草协议或诉状。",
      "整理身份与婚姻状况证明；境外文件附认证翻译。",
      "在区公所登记或向法院提交。",
      "取得认证副本、翻译与认证，供国外使用。",
    ],
    checklist: [
      "结婚证书，必要时含婚前协议文件。",
      "子女的出生证明。",
      "双方泰国身份证、户口本与护照。",
      "与监护或扶养安排相关的证据。",
      "境外法院命令及其认证翻译。",
    ],
    pitfalls: [
      "协议离婚登记时未就财产与监护作出书面约定。",
      "境外命令未经承认程序即在泰国使用。",
      "用语导致区公所或银行事后拒绝执行。",
      "以为泰国与境外记录会自动同步——并不会。",
    ],
    remote: "起草与证据审核可远程；登记与开庭需到场。",
  },
  accounting: {
    label: "会计、税务与审计文件服务",
    definition:
      "会计文件工作涵盖账簿记录、财务报表、审计档案与税务申报，以及银行、投标委员会或海外母公司要求时的认证与翻译。",
    chain: [
      "确认索要方需要哪些报表与期间。",
      "核对账务并编制报表组。",
      "需要经审计报表时由审计师签核。",
      "对报表与审计附注办理认证翻译。",
      "由公证律师认证；赴外使用再办理外交部认证。",
    ],
    checklist: [
      "公司证明书与税务登记文件。",
      "该期间的试算表、总账与银行对账单。",
      "上一年度已申报报表与纳税申报书。",
      "索要方的格式要求。",
    ],
    pitfalls: [
      "对方要求经审计报表，却提交管理报表。",
      "译文丢失审计师附注的原有用语。",
      "对随后被重述的中期版本先行认证。",
      "在收集文件期间错过申报期限。",
    ],
    remote: "可全程远程办理，纸质认证件以快递寄送。",
  },
  interpreter: {
    label: "法庭与预约口译服务",
    definition:
      "口译是在现场提供的口语支持——法院开庭、土地厅、区公所、公证签字、银行或移民柜台。它与文件的认证翻译是两回事，许多场合两者都需要。",
    chain: [
      "确认地点、语言对、日期与预计时长。",
      "提前向译员说明案件背景与专业术语。",
      "提供文件组，使口译术语与书面档案一致。",
      "译员随同出席该预约。",
      "会后纪要，以及场所要求的书面翻译。",
    ],
    checklist: [
      "地点、房号与预约时间。",
      "语言对，必要时说明方言。",
      "需要讨论的文件（请提前提供）。",
      "出席各方的姓名与身份，以便使用正确称谓。",
    ],
    pitfalls: [
      "特殊语言对预约过晚。",
      "未提前提供文件，导致术语在柜台临场发挥。",
      "以为口译员可当场同时出具认证书面翻译。",
      "低估时长，影响后续排程。",
    ],
    remote: "在场所允许时可远程口译；法院与登记机关通常要求到场。",
  },
  embassy: {
    label: "使馆与领事文件服务",
    definition:
      "对许多赴外文件而言，使馆认证是最后一环：在外交部认证之后，由目的国驻泰使馆再行认证。各使馆的预约方式、用语与指定译员要求各不相同。",
    chain: [
      "确认使馆当前要求，包括是否有指定译员名单。",
      "据此准备泰文原件与认证翻译。",
      "使馆要求时先完成外交部认证。",
      "预约并递交使馆，领取认证件。",
      "交付时附递交清单，便于接收机关核对。",
    ],
    checklist: [
      "带签字与印章的泰文原件。",
      "译成所需语言的认证翻译。",
      "文件持有人的护照或身份证。",
      "使馆表格与授权信。",
    ],
    pitfalls: [
      "使用使馆指定名单以外的译员。",
      "在外交部认证之前先递交使馆。",
      "遗漏使馆特有的表格或照片要求。",
      "以为所有使馆流程相同——并不相同。",
    ],
    remote: "在使馆允许代理的情况下，准备与递交都可由我们处理。",
  },
  ip: {
    label: "商标、版权与知识产权文件服务",
    definition:
      "泰国的知识产权工作以在知识产权厅（DIP）申请与维护商标为核心，另包括版权备案以及转让或许可文件——境外签署时通常需要公证认证与领事认证。",
    chain: [
      "申请前做检索与类别选择。",
      "准备申请书、样标与授权书。",
      "提交并跟踪审查，回应审查意见。",
      "对境外签署的授权书、转让或许可办理认证与领事认证。",
      "在到期时办理转让、许可备案与续展。",
    ],
    checklist: [
      "与实际使用形式完全一致的商标样标。",
      "申请人的公司文件或身份证明。",
      "用于确定类别的商品与服务清单。",
      "已签署的授权书（境外签署需公证与领事认证）。",
      "主张在先使用时的使用证据。",
    ],
    pitfalls: [
      "未做检索即提交，随后遭遇异议。",
      "类别清单过窄，无法覆盖实际业务。",
      "境外签署的授权书缺少所需认证。",
      "错过续展或答复期限。",
    ],
    remote: "申请与审查程序可远程办理；仅签字认证可能需到场。",
  },
  litigation: {
    label: "诉讼支持与法院文件服务",
    definition:
      "诉讼支持涵盖诉状、证据组、外文证据的认证翻译、经公证的宣誓书与授权书——按法院接受的形式准备，避免因形式瑕疵被驳。",
    chain: [
      "案件评估：管辖法院、时效与证据缺口。",
      "按法院格式准备诉状与证据册。",
      "对每份外文证据办理认证翻译。",
      "对境外签署文件办理公证认证与领事认证。",
      "提交、由律师出庭，以及判后各项程序。",
    ],
    checklist: [
      "合同、往来函件与完整文件链。",
      "各方护照或公司文件。",
      "委托律师的授权书（境外签署须经认证）。",
      "境外判决或仲裁裁决及其认证翻译。",
    ],
    pitfalls: [
      "外文证据未附认证泰文翻译即提交。",
      "境外签署的授权书未经公证与领事认证。",
      "在收集证据期间丧失时效。",
      "期待结果保证——任何律师都无法给出。",
    ],
    remote: "起草与证据工作可远程；开庭由律师出席。",
  },
  pdpa: {
    label: "PDPA 个人资料保护文件服务",
    definition:
      "泰国《个人资料保护法》（PDPA）要求处理个人资料须有书面依据：隐私声明、同意记录、资料处理协议、处理活动记录与外泄处理程序。",
    chain: [
      "资料盘点：持有哪些个人资料、目的为何、流向何处。",
      "为每一处理目的选定并记录合法依据。",
      "起草隐私声明、同意书与资料处理协议。",
      "建立处理活动记录与资料外泄处理程序。",
      "员工培训，并随业务变化定期复核。",
    ],
    checklist: [
      "持有个人资料的系统与供应商清单。",
      "现有隐私声明与同意用语。",
      "涉及资料处理的供应商合同。",
      "如有跨境传输，请提供其细节。",
    ],
    pitfalls: [
      "直接照搬境外 GDPR 声明，未对应 PDPA 要求。",
      "同意条款被捆绑，导致无法作为依据。",
      "没有处理活动记录——这是稽核最先索要的文件。",
      "供应商合同缺少资料处理条款。",
    ],
    remote: "可全程远程，工作坊以视频会议进行。",
  },
  "misc-official": {
    label: "其他官方文件服务",
    definition:
      "此类涵盖无法归入单一部委的官方文件：各类执照、许可、证明、记录调取，以及在泰国或国外使用时的认证与翻译。",
    chain: [
      "确认出具机关及其现行表格。",
      "整理证据清单并核对身份文件。",
      "提交并跟踪受理编号。",
      "文件出境时办理认证翻译。",
      "必要时办理外交部认证与使馆认证。",
    ],
    checklist: [
      "申请人的护照或泰国身份证。",
      "既有证明、执照或受理编号。",
      "机关表格上列明的支持证据。",
      "由我们代为提交时需授权书。",
    ],
    pitfalls: [
      "递交到错误机关，导致重新排队。",
      "证据清单不完整。",
      "泰文原件尚未定稿就先翻译。",
      "忽略支持文件的有效期。",
    ],
    remote: "在允许代理的范围内，提交与跟进由我们处理。",
  },
  council: {
    label: "律师公会认证服务",
    definition:
      "部分认证须由泰国律师公会（Lawyers Council of Thailand）注册律师办理——包括公证律师业务，以及律师对赴外使用文件与副本的认证。",
    chain: [
      "确认接收机关要认证什么、用何种用语。",
      "核验签署人身份与签署权限。",
      "由律师公会注册律师认证并登入登记簿。",
      "必要时办理外交部认证与目的国使馆认证。",
      "交付认证正本并附核对清单。",
    ],
    checklist: [
      "每位签署人的护照或泰国身份证。",
      "尚未签署、用语已定稿的文件。",
      "公司签署所需的授权文件。",
      "已取得的接收机关书面要求。",
    ],
    pitfalls: [
      "在认证预约前先行签署文件。",
      "对方要求正本，却只认证副本。",
      "姓名拼写与护照不一致。",
      "目的国要求领事认证却省略该环节。",
    ],
    remote: "审核与后续环节可远程；签字见证需当面办理。",
  },
  "due-diligence": {
    label: "尽职调查与核验服务",
    definition:
      "尽职调查用于核实对方的陈述：公司存续状态、董事与股东、土地产权、诉讼风险与文件真伪——依据登记机关记录，而非对方的口头保证。",
    chain: [
      "界定范围：主体、资产与需要回答的风险问题。",
      "在可查范围内调取登记记录（DBD、土地厅、法院索引）。",
      "核验文件真伪与签署权限。",
      "对关键发现与来源文件办理认证翻译。",
      "出具书面报告，按风险排序并给出后续建议。",
    ],
    checklist: [
      "对方名称、注册号与地址。",
      "地契或资产识别信息。",
      "合同草案与对方所作的陈述保证。",
      "对方已提供的既有文件。",
    ],
    pitfalls: [
      "仅凭对方提供的文件，未经登记机关核验。",
      "省略土地厅的抵押与权利负担查询。",
      "在报告完成前就签署。",
      "以为查询结果长期有效——记录会变动。",
    ],
    remote: "登记查询与报告可远程完成；实地查看可另行安排。",
  },
  pension: {
    label: "退休金与海外福利文件服务",
    definition:
      "海外退休金与福利机构会定期要求生存证明、住址证明、婚姻状况确认或银行核验，且往往须经认证与领事认证后才会受理。",
    chain: [
      "确认该机构要求的确切表格与认证等级。",
      "填写表格并准备身份与住址证明。",
      "由公证律师或律师认证签字与副本。",
      "机构要求时办理外交部认证与使馆认证。",
      "提交或快递至该机构并确认收件。",
    ],
    checklist: [
      "护照与泰国居留或移民文件。",
      "该机构的表格，尚未签署。",
      "泰国住址证明。",
      "福利与婚姻状况相关时的结婚或身份文件。",
      "按要求格式提供的银行信息。",
    ],
    pitfalls: [
      "在认证预约前先签署生存证明。",
      "错过年度期限，可能导致停发。",
      "认证等级低于机构要求。",
      "住址证明与移民记录不一致。",
    ],
    remote: "除签字见证外，其余均可由我们代办。",
  },
  "work-permit": {
    label: "工作证（Work Permit）文件服务",
    definition:
      "泰国工作证由劳动就业厅依雇主与雇员两套证据核发。我们负责文件准备与顺序安排，并就相关签证环节提供建议；核发决定权仍在主管机关。",
    chain: [
      "确认工作证类型、雇主资格与现行文件清单。",
      "准备雇主文件：公司证明书、财务报表、员工比例证明。",
      "准备雇员文件：学历、推荐信、体检证明、照片。",
      "对境外学历办理认证翻译与领事认证（如有要求）。",
      "提交、跟进，并使工作证与签证／延期时间线对齐。",
    ],
    checklist: [
      "雇主公司证明书、DBD 摘录与增值税登记。",
      "雇员护照及正确签证类别。",
      "学位证书与成绩单及其认证翻译。",
      "符合现行规格的体检证明。",
      "劳动合同与职位说明。",
    ],
    pitfalls: [
      "以旅游签入境，而该路径需要其他签证类别。",
      "境外学历缺少认证翻译与领事认证。",
      "提交时雇主的员工比例或注册资本未达要求。",
      "工作证与签证到期日不一致。",
    ],
    remote: "准备与提交支持可远程；体检与柜台环节需本人到场。",
  },
  expert: {
    label: "复杂个案与专项文件服务",
    definition:
      "有些案件无法套用标准流程：多国认证链、机关用语特殊、曾被退件，或需要按特定顺序完成多道认证。这类案件以顾问主导方式处理。",
    chain: [
      "个案审阅：接收机关的书面要求究竟是什么。",
      "设计认证链以及必须遵循的顺序。",
      "在进一步认证前先修正既有文件的瑕疵。",
      "执行整条链：翻译、公证认证、外交部认证、使馆认证。",
      "交付时附递交备忘录，便于接收机关按步核对。",
    ],
    checklist: [
      "已产生的全部文件，包括被退回的版本。",
      "书面退件理由或要求函（如有）。",
      "各方护照或公司文件。",
      "目的国家、接收机关与截止日期。",
    ],
    pitfalls: [
      "未处理退件原因就重复同一条认证链。",
      "认证顺序错误，导致最终版本未被覆盖。",
      "依据二手转述的要求，而非机关本身的用语。",
      "复杂案件未预留第二轮的时间。",
    ],
    remote: "评估与执行可远程；仅见证环节需到场。",
  },
};
